import type { Polygon, Position } from 'geojson';
import type { MapRef } from 'react-map-gl';
import { SURVEY_FILL_LAYER } from './survey-areas-layer';

/**
 * Everything a vertex may snap onto — "both, whichever is nearer" per the UAT 16
 * decision: the survey grid AND any existing area boundary on screen.
 *
 * The grid side needs two sources because they cover different ground: the
 * survey tile carries the areas that can be *picked* — Texas abstracts and their
 * tracts — and stops there, while the shared map's section layers
 * (`components/map/land-grid.tsx`) are served nationwide. The area side names
 * the shared map's development-area fills; the rest of the visible areas are
 * matched by prefix below, since their layer ids carry the area's own id.
 */
export const SNAP_LAYER_IDS = [
  SURVEY_FILL_LAYER,
  'Section',
  'Texas Sections',
  'development-area-horizontal-fill',
  'development-area-vertical-fill',
];

/**
 * Layer-id prefixes for the per-area layers: `PolygonArea` renders every area as
 * `polygon-fill-<id>`, and the Focus Areas tiles as `focus-area-fill-<uuid>` —
 * ids that can't be enumerated up front, so they're matched against the style.
 */
export const SNAP_LAYER_PREFIXES = ['polygon-fill-', 'focus-area-fill-'];

/**
 * The one `polygon-fill-` layer never snapped to: the backend-derived preview of
 * the shape being built. It follows the inputs being edited, so snapping onto it
 * would chase the shape's own echo.
 */
const PREVIEW_LAYER_ID = 'polygon-fill-area-preview';

/**
 * How close a vertex has to land, in screen pixels, before it is pulled onto a
 * section line. Screen pixels rather than feet so the pull feels the same at
 * every zoom — the same reason drawing tools everywhere work this way.
 *
 * The v8 mock uses 14, which in practice left hand-drawn clicks 15–20 px out
 * unsnapped — close enough to look like a miss, and enough of them to make an
 * edge along a section line hard to draw. 25 is forgiving enough that two
 * vertices meant for the same line both reach it, and the preview shows the pull
 * before a click commits it.
 */
export const SNAP_TOLERANCE_PX = 25;

/**
 * How close a *corner* has to be to win outright over a nearer edge. Tighter
 * than the edge tolerance on purpose: at 25 px a corner would hijack a vertex
 * deliberately placed part-way along a boundary, dragging it up to a section
 * corner it was nowhere near.
 */
export const CORNER_TOLERANCE_PX = 12;

interface Pixel {
  x: number;
  y: number;
}

/**
 * The closest point to `point` on the segment `a`–`b`, all in pixel space.
 *
 * Segments rather than endpoints, so a vertex can land part-way along a section
 * boundary; corners still win when they are nearest, being segment ends.
 *
 * @param {Pixel} point - The point being snapped.
 * @param {Pixel} a - Start of the segment.
 * @param {Pixel} b - End of the segment.
 * @return {Pixel} The closest point on the segment.
 */
export const nearestPointOnSegment = (
  point: Pixel,
  a: Pixel,
  b: Pixel
): Pixel => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;

  // A zero-length segment (duplicated vertex) has only one candidate point.
  if (lengthSquared === 0) {
    return a;
  }

  // Position along the segment of the perpendicular from `point`, clamped so the
  // result stays between the two ends rather than running off the line.
  const t = Math.max(
    0,
    Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared)
  );

  return { x: a.x + t * dx, y: a.y + t * dy };
};

const distance = (a: Pixel, b: Pixel) => Math.hypot(a.x - b.x, a.y - b.y);

interface Candidate {
  point: Pixel;
  away: number;
}

/**
 * The closest candidate inside the tolerance, or null when none is.
 *
 * @param {Candidate[]} candidates - Points the vertex could snap to.
 * @param {number} tolerancePx - How close counts, in screen pixels.
 * @return {Candidate | null} The winner, or null.
 */
const nearest = (candidates: Candidate[], tolerancePx: number) =>
  candidates.reduce<Candidate | null>((closest, candidate) => {
    const closer = !closest || candidate.away < closest.away;
    return candidate.away < tolerancePx && closer ? candidate : closest;
  }, null);

/**
 * Every line of a feature, as coordinate lists — polygon rings and line strings
 * alike, since the grid is drawn as lines in some layers and fills in others.
 *
 * @param {object} geometry - The feature's geometry.
 * @return {Array<Position[]>} Its lines, or none for other geometry types.
 */
const linesOf = (geometry: {
  type: string;
  coordinates: unknown;
}): Position[][] => {
  if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
    return geometry.coordinates as Position[][];
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as Position[][][]).flat();
  }
  if (geometry.type === 'LineString') {
    return [geometry.coordinates as Position[]];
  }
  return [];
};

interface SnapOptions {
  /**
   * Layers holding the grid, queried for something to snap onto. Layers absent
   * from the style are skipped, so a caller can name every grid it might have.
   */
  layerIds: string[];
  /**
   * Prefixes matched against the style's layers, for per-area layers whose ids
   * carry the area's own id and can't be named up front. Optional; the preview
   * layer is always left out.
   */
  layerPrefixes?: string[];
  tolerancePx?: number;
  cornerTolerancePx?: number;
}

/**
 * The layers a snap may query: the named ones that exist, plus every style
 * layer matching a prefix — except the preview of the shape being built.
 *
 * @param {MapRef} map - The live map.
 * @param {string[]} layerIds - Exact layer ids to include when present.
 * @param {string[]} layerPrefixes - Prefixes to match against the style.
 * @return {string[]} The layers to hit-test.
 */
const resolveSnapLayers = (
  map: MapRef,
  layerIds: string[],
  layerPrefixes: string[]
): string[] => {
  const exact = layerIds.filter((id) => map.getLayer(id));
  if (layerPrefixes.length === 0) {
    return exact;
  }
  const styled = (map.getStyle?.()?.layers ?? [])
    .map((layer) => layer.id)
    .filter(
      (id) =>
        id !== PREVIEW_LAYER_ID &&
        layerPrefixes.some((prefix) => id.startsWith(prefix))
    );
  return [...exact, ...styled];
};

/**
 * Pulls one position onto the nearest survey-grid line, or leaves it alone when
 * nothing is close enough.
 *
 * @param {MapRef} map - The live map, for projecting and hit-testing.
 * @param {Position} position - The `[lng, lat]` being snapped.
 * @param {SnapOptions} options - Layer to snap to and how close counts.
 * @return {Position} The snapped position, or the original.
 */
export const snapPositionToSurveyGrid = (
  map: MapRef,
  position: Position,
  {
    layerIds,
    layerPrefixes = [],
    tolerancePx = SNAP_TOLERANCE_PX,
    cornerTolerancePx = CORNER_TOLERANCE_PX,
  }: SnapOptions
): Position => {
  const layers = resolveSnapLayers(map, layerIds, layerPrefixes);
  if (layers.length === 0) {
    return position;
  }

  const origin = map.project([position[0], position[1]]);
  const box: [[number, number], [number, number]] = [
    [origin.x - tolerancePx, origin.y - tolerancePx],
    [origin.x + tolerancePx, origin.y + tolerancePx],
  ];

  const features = map.queryRenderedFeatures(box, { layers });
  const corners: Candidate[] = [];
  const edges: Candidate[] = [];

  features.forEach((feature) => {
    if (!feature.geometry) return;
    linesOf(feature.geometry as { type: string; coordinates: unknown }).forEach(
      (ring) => {
        for (let index = 0; index < ring.length - 1; index += 1) {
          const a = map.project([ring[index][0], ring[index][1]]);
          const b = map.project([ring[index + 1][0], ring[index + 1][1]]);
          corners.push({ point: a, away: distance(origin, a) });
          const point = nearestPointOnSegment(origin, a, b);
          edges.push({ point, away: distance(origin, point) });
        }
      }
    );
  });

  // The box query returns whole sections, so most of their edges are nowhere near
  // the vertex — only the closest, and only inside the tolerance, may move it.
  //
  // A corner close enough wins outright, even over a nearer edge: it is the point
  // a boundary is meant to meet, and neighbouring areas share an exact corner
  // only if both land on it. The v8 mock resolves it the same way, on one radius;
  // ours reaches further along edges, so corners keep their own tighter one.
  const best =
    nearest(corners, Math.min(cornerTolerancePx, tolerancePx)) ??
    nearest(edges, tolerancePx);

  if (!best) {
    return position;
  }

  const snapped = map.unproject([best.point.x, best.point.y]);
  return [snapped.lng, snapped.lat];
};

/**
 * Snaps every vertex of every polygon onto the survey grid where one is close by.
 *
 * Applied to the whole set each time, so editing an earlier shape snaps too.
 * Already-snapped vertices sit on a line, so re-running leaves them alone.
 *
 * @param {MapRef | null} map - The live map, or null before it loads.
 * @param {Polygon[]} polygons - The drawn polygons.
 * @param {SnapOptions} options - Layer to snap to and how close counts.
 * @return {Polygon[]} The polygons with their vertices snapped.
 */
export const snapPolygonsToSurveyGrid = (
  map: MapRef | null,
  polygons: Polygon[],
  options: SnapOptions
): Polygon[] => {
  if (!map) {
    return polygons;
  }

  return polygons.map((polygon) => ({
    ...polygon,
    coordinates: polygon.coordinates.map((ring) => {
      const snapped = ring.map((position) =>
        snapPositionToSurveyGrid(map, position, options)
      );
      // A ring has to close on itself, and its first and last vertex may have
      // snapped to different lines, so the close is restated rather than snapped.
      if (snapped.length > 1) {
        snapped[snapped.length - 1] = snapped[0];
      }
      return snapped;
    }),
  }));
};
